import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
    ArrowDown,
    BadgeCheck,
    BriefcaseBusiness,
    Check,
    CheckCircle2,
    CreditCard,
    Eye,
    Heart,
    Mail,
    MapPin,
    Pencil,
    Phone,
    Search,
    ShieldAlert,
    SlidersHorizontal,
    UserCheck,
    UserRound,
    UsersRound,
} from 'lucide-react';
import toast from 'react-hot-toast';
import Sidebar from '../../components/Sidebar';
import Navbar from '../../components/Navbar';
import SafeImage from '../../components/SafeImage';
import Select from '../../components/Select';
import API from '../../utils/api';
import { aadharStatusMeta, initialFor, mediaUrl } from './profileShared';
import './Profiles.css';

const PAGE_SIZE = 9;

const Profiles = () => {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const [profiles, setProfiles] = useState([]);
    const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);
    // /profiles?q=<mobile>&open=1 comes from the Users page: the person is searched here and their detail opens
    const [query, setQuery] = useState(searchParams.get('q') || '');
    const [gender, setGender] = useState('all');
    const [verification, setVerification] = useState('all');

    useEffect(() => { fetchProfiles(); }, []);

    const fetchProfiles = async () => {
        try {
            const res = await API.get('/admin/profiles');
            setProfiles(res.data.profiles || []);
        } catch (err) {
            toast.error('Failed to load profiles');
        }
    };

    const handleVerify = async (id) => {
        try {
            await API.put(`/admin/profiles/${id}/verify`);
            toast.success('Profile verified!');
            fetchProfiles();
        } catch (err) {
            toast.error(err.response?.data?.message || 'Failed to verify');
        }
    };

    const stats = useMemo(() => ({
        total: profiles.length,
        verified: profiles.filter(profile => profile.isVerified).length,
        awaitingAadhar: profiles.filter(profile => profile.aadharStatus === 'pending').length,
        needsVerification: profiles.filter(profile => !profile.isVerified).length,
    }), [profiles]);

    const pendingAadhar = useMemo(() => profiles.filter(profile => profile.aadharStatus === 'pending'), [profiles]);

    // a chip under the search box: show only that member, so the Aadhaar can be reviewed straight away
    const focusProfile = (profile) => {
        setGender('all');
        setVerification('all');
        setQuery(profile.memberId || profile.user?.mobile || profile.name || '');
        setVisibleCount(PAGE_SIZE);
    };

    const filteredProfiles = useMemo(() => {
        const normalizedQuery = query.trim().toLowerCase();

        return profiles.filter(profile => {
            const searchable = [
                profile.memberId,
                profile.name,
                profile.user?.name,
                profile.user?.mobile,
                profile.user?.email,
                profile.religion,
                profile.caste,
                profile.occupation,
                profile.city,
            ].filter(Boolean).join(' ').toLowerCase();

            const matchesQuery = !normalizedQuery || searchable.includes(normalizedQuery);
            const matchesGender = gender === 'all' || profile.gender?.toLowerCase() === gender;
            const matchesVerification = verification === 'all'
                || (verification === 'verified' && profile.isVerified)
                || (verification === 'unverified' && !profile.isVerified);

            return matchesQuery && matchesGender && matchesVerification;
        });
    }, [gender, profiles, query, verification]);

    useEffect(() => {
        setVisibleCount(PAGE_SIZE);
    }, [query, gender, verification]);

    useEffect(() => {
        if (searchParams.get('open') !== '1' || !profiles.length) return;
        const wanted = (searchParams.get('q') || '').trim().toLowerCase();
        const hits = profiles.filter(profile => [profile.memberId, profile.user?.mobile, profile.user?.name, profile.name].some(v => String(v || '').toLowerCase() === wanted));
        if (hits.length === 1) navigate(`/profiles/${hits[0]._id}`, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [profiles]);

    const visibleProfiles = filteredProfiles.slice(0, visibleCount);
    const filtersActive = query || gender !== 'all' || verification !== 'all';

    const clearFilters = () => {
        setQuery('');
        setGender('all');
        setVerification('all');
    };

    return (
        <div className="profiles-layout">
            <Sidebar />
            <div className="admin-content profiles-content">
                <Navbar title={<span className="profiles-nav-title"><Heart size={18} /> Profiles</span>} />

                <main className="profiles-page">
                    <header className="profiles-hero">
                        <div className="profiles-hero__copy">
                            <span className="profiles-eyebrow"><UsersRound size={14} /> Member management</span>
                            <h1>Matrimony profiles</h1>
                            <p>Review member details, validate identity and keep every profile trusted and match-ready.</p>
                        </div>
                        <div className="profiles-hero__ornament" aria-hidden="true">
                            <Heart size={58} strokeWidth={1.15} />
                        </div>
                    </header>

                    <section className="profiles-stats" aria-label="Profile summary">
                        <article className="profiles-stat">
                            <span className="profiles-stat__icon profiles-stat__icon--total"><UsersRound size={20} /></span>
                            <div><strong>{stats.total}</strong><span>Total profiles</span></div>
                        </article>
                        <article className="profiles-stat">
                            <span className="profiles-stat__icon profiles-stat__icon--verified"><UserCheck size={20} /></span>
                            <div><strong>{stats.verified}</strong><span>Verified profiles</span></div>
                        </article>
                        <article className="profiles-stat">
                            <span className="profiles-stat__icon profiles-stat__icon--review"><CreditCard size={20} /></span>
                            <div><strong>{stats.awaitingAadhar}</strong><span>Aadhaar reviews</span></div>
                        </article>
                        <article className="profiles-stat">
                            <span className="profiles-stat__icon profiles-stat__icon--pending"><ShieldAlert size={20} /></span>
                            <div><strong>{stats.needsVerification}</strong><span>Awaiting verification</span></div>
                        </article>
                    </section>

                    <section className="profiles-toolbar" aria-label="Profile filters">
                        <label className="profiles-search">
                            <Search size={18} />
                            <input
                                type="search"
                                value={query}
                                onChange={event => setQuery(event.target.value)}
                                placeholder="Search by name, mobile, email or city…"
                                aria-label="Search profiles"
                            />
                        </label>

                        <div className="profiles-filter-group">
                            <SlidersHorizontal size={17} className="profiles-filter-icon" />
                            <Select
                                className="profiles-select"
                                value={gender}
                                onChange={setGender}
                                allowEmpty={false}
                                ariaLabel="Filter profiles by gender"
                                options={[
                                    { value: 'all', label: 'All genders' },
                                    { value: 'female', label: 'Women' },
                                    { value: 'male', label: 'Men' },
                                ]}
                            />
                            <Select
                                className="profiles-select"
                                value={verification}
                                onChange={setVerification}
                                allowEmpty={false}
                                ariaLabel="Filter profiles by verification status"
                                options={[
                                    { value: 'all', label: 'All statuses' },
                                    { value: 'verified', label: 'Verified' },
                                    { value: 'unverified', label: 'Unverified' },
                                ]}
                            />
                            {filtersActive && <button className="profiles-clear" type="button" onClick={clearFilters}>Clear</button>}
                        </div>
                    </section>

                    {pendingAadhar.length > 0 && (
                        <div className="profiles-pending" aria-label="Aadhaar waiting for review">
                            <span className="profiles-pending__label"><CreditCard size={15} /> Aadhaar waiting for review</span>
                            {pendingAadhar.map(profile => (
                                <button
                                    key={profile._id}
                                    type="button"
                                    className={`profiles-pending__chip${query && query === (profile.memberId || profile.user?.mobile || profile.name) ? ' is-active' : ''}`}
                                    onClick={() => focusProfile(profile)}
                                >
                                    {profile.name || profile.user?.name || profile.memberId}
                                </button>
                            ))}
                        </div>
                    )}

                    <div className="profiles-results-heading">
                        <div>
                            <h2>Profile directory</h2>
                            <p>{filteredProfiles.length} {filteredProfiles.length === 1 ? 'profile' : 'profiles'} found</p>
                        </div>
                    </div>

                    <section className="profiles-grid" aria-live="polite">
                        {visibleProfiles.map(profile => {
                            const displayName = profile.name || profile.user?.name || 'Unnamed member';
                            const aadhar = aadharStatusMeta(profile.aadharStatus);
                            const genderClass = profile.gender?.toLowerCase() === 'female' ? 'female' : 'male';
                            const personalLine = [profile.age ? `${profile.age} yrs` : null, profile.religion, profile.caste].filter(Boolean).join(' · ');
                            const professionLine = [profile.occupation, profile.city].filter(Boolean).join(' · ');

                            return (
                                <article key={profile._id} className={`profile-card profile-card--${genderClass}`}>
                                    <div className="profile-card__accent" />

                                    <div className="profile-card__header">
                                        <div className="profile-avatar">
                                            {profile.photo ? (
                                                <SafeImage
                                                    src={mediaUrl(profile.photo)}
                                                    alt={displayName}
                                                    className="profile-avatar__image"
                                                    fallback={<span className="profile-avatar__initial" aria-hidden="true">{initialFor(displayName)}</span>}
                                                />
                                            ) : (
                                                <span className="profile-avatar__initial">{initialFor(displayName)}</span>
                                            )}
                                            {profile.isVerified && <span className="profile-avatar__verified" title="Verified profile"><Check size={11} /></span>}
                                        </div>

                                        <div className="profile-card__identity">
                                            <div className="profile-card__name-row">
                                                <h3>{displayName}</h3>
                                                {profile.isVerified && <span className="profile-verified-pill"><BadgeCheck size={13} /> Verified</span>}
                                            </div>
                                            <p>{personalLine || 'Profile details not added'}</p>
                                            <span className="profile-for"><UserRound size={11} /> Member ID · {profile.memberId || 'Pending assignment'}</span>
                                            {profile.profileFor && profile.profileFor !== 'Myself' && (
                                                <>
                                                    <span className="profile-for profile-for--wrap" title={`Profile for ${profile.profileFor}: ${displayName}`}>
                                                        <UserRound size={11} /> For {profile.profileFor} · {displayName}
                                                    </span>
                                                    <span className="profile-for profile-for--wrap" title="Who created this profile">
                                                        <UserRound size={11} /> Created by {profile.createdByName || profile.user?.name || 'account holder'}
                                                    </span>
                                                </>
                                            )}
                                            {profile.customFields?.length > 0 && (
                                                <span className="profile-custom-flag" title="This profile contains member-entered custom values">
                                                    <Pencil size={10} /> Custom details
                                                </span>
                                            )}
                                        </div>
                                    </div>

                                    <div className="profile-contact">
                                        <div><Phone size={15} /><span>{profile.user?.mobile || 'Mobile not provided'}</span></div>
                                        <div><Mail size={15} /><span title={profile.user?.email}>{profile.user?.email || 'Email not provided'}</span></div>
                                    </div>

                                    <div className="profile-card__details">
                                        <div className="profile-detail-row">
                                            <span className="profile-detail-row__icon"><BriefcaseBusiness size={15} /></span>
                                            <div><small>Profession</small><strong>{profile.occupation || 'Not specified'}</strong></div>
                                        </div>
                                        <div className="profile-detail-row">
                                            <span className="profile-detail-row__icon"><MapPin size={15} /></span>
                                            <div><small>Location</small><strong>{profile.city || 'Not specified'}</strong></div>
                                        </div>
                                    </div>

                                    {professionLine && <span className="sr-only">{professionLine}</span>}

                                    <div className="profile-card__status">
                                        <span className="profile-status-label">Identity status</span>
                                        <span className="profile-aadhar-pill" style={{ '--status-color': aadhar.color, '--status-bg': aadhar.bg }}>
                                            <CreditCard size={13} /> Aadhaar: {aadhar.label}
                                        </span>
                                    </div>

                                    <div className="profile-card__actions">
                                        <button className="profile-action profile-action--primary" type="button" onClick={() => navigate(`/profiles/${profile._id}`)}>
                                            <Eye size={16} /> View profile
                                        </button>
                                        <button className="profile-action profile-action--edit" type="button" onClick={() => navigate(`/profiles/${profile._id}/edit`)}>
                                            <Pencil size={15} /> Edit
                                        </button>
                                    </div>

                                    {!profile.isVerified && (
                                        <div className="profile-card__verification is-ready">
                                            <button type="button" onClick={() => handleVerify(profile._id)}>
                                                <CheckCircle2 size={15} /> Verify profile separately
                                            </button>
                                        </div>
                                    )}
                                </article>
                            );
                        })}
                    </section>

                    {filteredProfiles.length === 0 && (
                        <section className="profiles-empty">
                            <span><Search size={28} /></span>
                            <h2>No matching profiles</h2>
                            <p>Try another search term or reset the selected filters.</p>
                            {filtersActive && <button type="button" onClick={clearFilters}>Clear all filters</button>}
                        </section>
                    )}

                    {filteredProfiles.length > 0 && (
                        <div className="profiles-load-more">
                            <div className="profiles-load-more__copy">
                                <span>Showing {visibleProfiles.length} of {filteredProfiles.length} profiles</span>
                                <div className="profiles-progress"><span style={{ width: `${(visibleProfiles.length / filteredProfiles.length) * 100}%` }} /></div>
                            </div>
                            {visibleCount < filteredProfiles.length && (
                                <button type="button" onClick={() => setVisibleCount(count => Math.min(filteredProfiles.length, count + PAGE_SIZE))}>
                                    <ArrowDown size={16} /> Load more profiles
                                </button>
                            )}
                        </div>
                    )}
                </main>
            </div>
        </div>
    );
};

export default Profiles;
